<script setup>
import { computed, ref } from 'vue';
import BaseBubble from 'next/message/bubbles/Base.vue';
import FormattedContent from './FormattedContent.vue';
import AttachmentChips from 'next/message/chips/AttachmentChips.vue';
import TranslationToggle from 'dashboard/components-next/message/TranslationToggle.vue';
import { ATTACHMENT_TYPES, MESSAGE_TYPES } from '../../constants';
import { useMessageContext } from '../../provider.js';
import { useTranslations } from 'dashboard/composables/useTranslations';

const { content, attachments, contentAttributes, messageType } =
  useMessageContext();

const { hasTranslations, translationContent } =
  useTranslations(contentAttributes);

const renderOriginal = ref(false);

const renderContent = computed(() => {
  if (renderOriginal.value) {
    return content.value;
  }

  if (hasTranslations.value) {
    return translationContent.value;
  }

  return content.value;
});

const isTemplate = computed(() => {
  return messageType.value === MESSAGE_TYPES.TEMPLATE;
});

const isEmpty = computed(() => {
  return !content.value && !attachments.value?.length;
});

const hasMediaAttachments = computed(() =>
  attachments.value?.some(attachment =>
    [ATTACHMENT_TYPES.IMAGE, ATTACHMENT_TYPES.VIDEO].includes(
      attachment.fileType
    )
  )
);

// Aceleres: badge "editada" (estilo WhatsApp). Fica no fluxo do texto, não
// no MessageMeta absoluto do BaseBubble — assim não vaza quando a mensagem
// é curta ("hola"). content_attributes.edited é setado pelo patch_content
// do backend em app/controllers/api/v1/accounts/conversations/messages_controller.rb.
const isEdited = computed(() => !!contentAttributes.value?.edited);

const handleSeeOriginal = () => {
  renderOriginal.value = !renderOriginal.value;
};
</script>

<template>
  <BaseBubble
    class="acl-text-bubble w-fit max-w-full break-words px-2.5 py-1.5"
    :class="{ 'acl-text-bubble--with-media': hasMediaAttachments }"
    data-bubble-name="text"
  >
    <div class="acl-message-content gap-2 flex flex-col">
      <span v-if="isEmpty" class="text-n-slate-11">
        {{ $t('CONVERSATION.NO_CONTENT') }}
      </span>
      <FormattedContent
        v-if="renderContent"
        :content="renderContent"
        :large-single-emoji="!attachments?.length"
        class="acl-message-caption"
      />
      <span
        v-if="isEdited"
        class="text-[11px] opacity-70 leading-tight -mt-1"
      >editada</span>
      <TranslationToggle
        v-if="hasTranslations"
        class="-mt-3"
        :showing-original="renderOriginal"
        @toggle="handleSeeOriginal"
      />
      <AttachmentChips :attachments="attachments" class="gap-2" />
      <template v-if="isTemplate">
        <div
          v-if="contentAttributes.submittedEmail"
          class="px-2 py-1 rounded-lg bg-n-alpha-3"
        >
          {{ contentAttributes.submittedEmail }}
        </div>
      </template>
    </div>
  </BaseBubble>
</template>

<style>
p:last-child {
  margin-bottom: 0;
}
</style>
